import React, { useEffect, useState } from 'react'
import { Box, Skeleton } from '@mui/material'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartSkeleton } from './ChartSkeleton'
import { formatDollarValue, formatTimestamp, useChartHeight } from './chartUtils'
import { commonTooltipProps } from './commonComponents'

import useIsMobile from '../../hooks/useIsMobile'
import { PrecentChange } from './PrecentChange'
import { TotalChartsOverview, TotalTVL, loadChartsTotalTVL } from './chartsApi'
import BigNumber from 'bignumber.js'

export function TvlChart(props: { className: string; overView: TotalChartsOverview }) {
    const { overView } = props
    const [tvlData, setTvlData] = useState([] as TotalTVL[])
    const [lowestValue, setLowestValue] = useState(0 as number)
    // const change = BigNumber(20) // todo replace with actual data

    useEffect(() => {
        if (tvlData && tvlData.length > 0) {
            const copyArr = [...tvlData]
            copyArr.sort((a, b) => {
                if (a.tvl > b.tvl) return -1
                if (b.tvl > a.tvl) return 1
                return 0
            })
            setLowestValue(copyArr[copyArr.length - 1].tvl)
        }
    }, [tvlData])

    const chartHeight = useChartHeight()
    const isMobile = useIsMobile()

    useEffect(() => {
        loadTvlData()
    }, [])

    async function loadTvlData() {
        const data = await loadChartsTotalTVL()
        if (data) setTvlData(data)
    }

    return (
        <div
            className='h-full bg-[#0C1647] pr-5 pl-3 py-6 rounded-2xl'
            style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
            <div className=''>
                TVL
                {overView && overView.tvl_change_7d && (
                    <Box sx={{ ml: 2, display: 'inline-flex' }}>
                        <PrecentChange value={BigNumber(overView.tvl_change_7d)} />
                    </Box>
                )}
            </div>
            <div className='text-[36px] font-bold text-white mb-5'>
                {tvlData && tvlData.length > 0 ? <>${formatDollarValue(tvlData[tvlData.length - 1].tvl)}</> : <Skeleton width={80} />}
            </div>

            {tvlData ? (
                <ResponsiveContainer width={'100%'} height={chartHeight}>
                    <AreaChart data={tvlData}>
                        <XAxis
                            domain={[`dataMin`, 'dataMax']}
                            dataKey='timestamp'
                            height={!isMobile ? 20 : 15}
                            type='number'
                            axisLine={{
                                stroke: '#549EFC',
                                strokeOpacity: 0.2,
                                strokeWidth: 1
                            }}
                            tick={{
                                fill: '#549EFC',
                                fontSize: !isMobile ? 14 : 12
                            }}
                            tickLine={{
                                stroke: '#549EFC',
                                strokeOpacity: 0.2,
                                strokeWidth: 1
                            }}
                            tickFormatter={formatTimestamp}
                            tickCount={5}
                            interval={'preserveStartEnd'}
                        />
                        {/* <YAxis domain={[lowestValue * 0.9, 'dataMax']} padding={{ top: !isMobile ? 70 : 50 }} hide={true} /> */}
                        <YAxis
                            allowDataOverflow={true}
                            tick={{
                                fill: '#549EFC',
                                fontSize: 12
                            }}
                            dx={-5}
                            tickLine={{
                                fill: '#549EFC',
                                strokeWidth: 0,
                                fillOpacity: 0.8
                            }}
                            axisLine={{ stroke: 'transparent' }}
                            tickFormatter={(x) => {
                                return `$${formatDollarValue(x, 2)}`
                            }}
                            // domain={['dataMin', 'dataMax']}
                            domain={['auto', 'auto']}
                            height={chartHeight}
                            hide={false}
                            padding={{ bottom: 0 }}
                        />
                        {/* <YAxis domain={[`dataMin`, 'dataMax']} padding={{ top: !isMobile ? 70 : 50 }} hide={true} /> */}
                        <CartesianGrid vertical={false} stroke='#0C3F6D' strokeDasharray='3' />
                        <Tooltip
                            cursor={{
                                stroke: '#549EFC',
                                strokeWidth: 1,
                                strokeDasharray: '1 2'
                            }}
                            {...commonTooltipProps(isMobile)}
                        />
                        <Area
                            type='linear'
                            dataKey='tvl'
                            strokeWidth={2}
                            stroke='#549EFC'
                            fill='transparent'
                            animationDuration={300}
                            activeDot={{
                                fill: '#549EFC',
                                r: 4,
                                stroke: '#549EFC',
                                strokeOpacity: 0.2,
                                strokeWidth: 8
                            }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            ) : (
                <ChartSkeleton />
            )}
        </div>
    )
}
